/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  VkCaptchaTokenPayload,
  VkInitRequest,
  VkInitResponse,
} from '@shared/Captcha/types.internal';
import {PageLoader} from '@shared/PageLoader/PageLoader';
import classNames from '@sindresorhus/class-names';
import Script from 'next/script';
import {useContext, useEffect, useRef, useState} from 'react';

import {CaptchaContext} from '../CaptchaContext';
import {
  createErrorMessageForAnalytics,
  useLogVkCaptchaEvents,
} from '../hooks/useLogVkCaptchaEvents';
import {ProviderProps} from './providerProps';
import styles from './VkCaptcha.module.scss';

const languageMapping = {
  ru: '0',
  en: '3',
} as const;

const sendInitRequest = async (
  apiEndpoint: string,
  placement: string,
): Promise<VkInitResponse> => {
  try {
    const initRequest: VkInitRequest = {source: placement};
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      body: JSON.stringify(initRequest),
      headers: {'content-type': 'application/json'},
      redirect: 'manual',
    });
    if (response.status !== 200) {
      return {
        error: true,
        message: createErrorMessageForAnalytics(
          response.type === 'opaqueredirect' ? 302 : response.status,
        ),
      };
    }
    return await response.json();
  } catch (e) {
    return {
      error: true,
      message: createErrorMessageForAnalytics(e instanceof Error ? e : 0),
    };
  }
};

export const VkCaptcha = ({
  placement,
  language,
  className,
  onResult,
  onError,
}: ProviderProps) => {
  const {vkInitEndpoint} = useContext(CaptchaContext);
  const {logInitResult, logSdkCallResult} = useLogVkCaptchaEvents();
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loaded) {
      if ((window as any).vkidCaptchaInit) {
        setLoaded(true);
      }
      return;
    }

    const doInitialization = async () => {
      const initResponse = await sendInitRequest(vkInitEndpoint, placement);

      if (initResponse.error) {
        logInitResult({
          placement,
          error: true,
          errorMessage: initResponse.message,
        });
        onError();
        return;
      }

      logInitResult({placement, error: false});
      const CaptchaSDK = await (window as any).vkidCaptchaInit;
      const sdkInstance = new CaptchaSDK();

      try {
        const token = await sdkInstance.show({
          container: containerRef.current,
          iframeSrc: initResponse.iframeUrl,
          view: 'block',
          scheme: 'light',
          lang: languageMapping[language ?? 'ru'],
        });
        const vkResult: VkCaptchaTokenPayload = {
          source: placement,
          sessionId: initResponse.sessionId,
          provider: 'vk',
          token,
        };
        logSdkCallResult({placement, error: false});
        onResult(vkResult);
      } catch (e) {
        console.warn('Failed', e);
        logSdkCallResult({
          placement,
          error: true,
          errorMessage: createErrorMessageForAnalytics(e),
        });
        onError();
      }
    };

    doInitialization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);
  return (
    <>
      <Script
        id="vk-captcha-script"
        src="https://static.vk.com/captchaSDK/loader/0/umd/index.js"
        strategy="afterInteractive"
        onLoad={() => setLoaded(true)}
        onError={() => {
          logInitResult({
            placement,
            error: true,
            errorMessage: 'script_load_error',
          });
          onError();
        }}
      />
      <div
        ref={containerRef}
        className={classNames(styles.container, className)}
      >
        <PageLoader className={styles.loader} />
      </div>
    </>
  );
};
